
import React, { Fragment, useEffect, useRef, useState } from 'react';

// Components

import SummaryItem from '../SummaryItem';
import MaskUnmaskIcon from '../MaskUnmaskData';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { toUTCAndFormat } from '@utils/dates/date.utils';

import { ELIGIBILITY_DOCUMENTS } from '@constants/ssap/hhmTypes/documentsOfEligibility';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { receiveImmigrationInfo } from '@actions/ssap/ssapActionCreators';
import { MASK_TIMEOUT, maskValue } from '@utils/miscellaneous/mask.utils';


const CitizenshipImmigrationStatusInfo = props => {

    const timeoutRef = useRef(null);
    const [imiMasked, setImiMasked] = useState({
        alienNumber: true,
        sevisId: true,
        foreignPassportOrDocumentNumber: true,
        i94Number: true,
        visaNumber: true
    });

    const {

        applyingForCoverageIndicator,
        citizenshipImmigrationStatus: {
            citizenshipStatusIndicator,
            eligibleImmigrationDocumentType,
            naturalizedCitizenshipIndicator,
            lawfulPresenceIndicator,
            livedIntheUSSince1996Indicator,
            citizenshipDocument: {
                alienNumber,
                sevisId,
                nameOnDocument,
                documentDescription,
                nameSameOnDocumentIndicator,
                visaNumber,
                documentExpirationDate,
                i94Number,
                foreignPassportOrDocumentNumber,
                foreignPassportCountryOfIssuance,
                cardNumber
            },
            otherImmigrationDocumentType: {
                orrcertificationIndicator,
                cubanHaitianEntrantIndicator,
                orreligibilityLetterIndicator,
                stayOfRemovalIndicator,
                americanSamoanIndicator,
                withholdingOfRemovalIndicator
            },
            unchangedImmigrationStatusLastFiveYears
        },
        idx
    } = props;

    if(!applyingForCoverageIndicator) {
        return null;
    }

    const getDocumentName = (documentType = [{ }]) => {
        const [ documentName = null ] = ELIGIBILITY_DOCUMENTS
            .filter(doc => documentType[0][doc.name]).map(doc => doc.text);
        return documentName;
    };

    const getDocumentExpirationDate = date => {
        if(date === null) {
            return date;
        }
        return toUTCAndFormat(date, MM_SLASH_DD_SLASH_YYYY);
    };

    const getValue = (val, key) => {
        return imiMasked[key] ? maskValue(val) : val;
    };

    const handleMaskToggle = (key, masked) => {
        setImiMasked(prev => ({ ...prev, [key]: masked }));

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (!masked) {
            // Set a new timeout to mask the data after 15 seconds
            timeoutRef.current = setTimeout(() => {
                setImiMasked(prev => ({ ...prev, [key]: true }));
                timeoutRef.current = null;
            }, MASK_TIMEOUT);
        }
    };

    const maskUnmaskIconProps = (key) => ({
        receiveData: receiveImmigrationInfo,
        masked: imiMasked[key],
        setMasked: (masked) => handleMaskToggle(key, masked) 
    });

    useEffect(() => {
        // Cleanup timeout on component unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    return (

        <Fragment>
            <SummaryItem
                textCode = 'common.are_you_us_citizen'
                value = { citizenshipStatusIndicator }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipStatusIndicator`
                        ]
                    ]
                }
            />

            <SummaryItem
                textCode = 'common.are_you_naturalized_citizen'
                value = { naturalizedCitizenshipIndicator }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.naturalizedCitizenshipIndicator`
                        ]
                    ]
                }
            />

            {
                naturalizedCitizenshipIndicator

                    ? (
                        <Fragment>
                            <SummaryItem
                                textCode = 'common.alien_number'
                                value={getValue(alienNumber, 'alienNumber')}
                                icon={<MaskUnmaskIcon {...maskUnmaskIconProps('alienNumber')} />}
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.alienNumber`,
                                            getValue,
                                            ['alienNumber']
                                        ]
                                    ]
                                }
                            />

                            <SummaryItem
                                textCode = 'common.naturalization_number'
                                value={getValue(sevisId, 'sevisId')}
                                icon={<MaskUnmaskIcon {...maskUnmaskIconProps('sevisId')} />}
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.sevisId`,
                                            getValue,
                                            ['sevisId']
                                        ]
                                    ]
                                }
                            />
                        </Fragment>
                    )
                    : null
            }

            {
                !citizenshipStatusIndicator &&

                <SummaryItem
                    textCode = 'common.immigration_status'
                    value = { lawfulPresenceIndicator }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.lawfulPresenceIndicator`
                            ]
                        ]
                    }
                />
            }
            {
                lawfulPresenceIndicator &&

                <Fragment>
                    {
                        getDocumentName(eligibleImmigrationDocumentType) &&

                        <SummaryItem
                            textCode = { getDocumentName(eligibleImmigrationDocumentType) }
                            value = 'common.no_text'
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.eligibleImmigrationDocumentType`,
                                        getDocumentName
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        alienNumber &&

                        <SummaryItem
                            textCode = 'common.alien_number'
                            value={getValue(alienNumber, 'alienNumber')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('alienNumber')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.alienNumber`,
                                        getValue,
                                        ['alienNumber']
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        sevisId &&

                        <SummaryItem
                            textCode = 'common.naturalization_number'
                            value={getValue(sevisId, 'sevisId')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('sevisId')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.sevisId`,
                                        getValue,
                                        ['sevisId']
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        documentDescription &&

                        <SummaryItem
                            textCode = 'common.document_description'
                            value = { documentDescription }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.documentDescription`
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        visaNumber &&

                        <SummaryItem
                            textCode = 'common.visa_number'
                            value={getValue(visaNumber, 'visaNumber')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('visaNumber')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.visaNumber`,
                                        getValue,
                                        ['visaNumber']
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        foreignPassportOrDocumentNumber &&

                        <SummaryItem
                            textCode = 'common.foreign_passport_number'
                            value={getValue(foreignPassportOrDocumentNumber, 'foreignPassportOrDocumentNumber')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('foreignPassportOrDocumentNumber')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.foreignPassportOrDocumentNumber`,
                                        getValue,
                                        ['foreignPassportOrDocumentNumber']

                                    ]
                                ]
                            }
                        />
                    }
                    {
                        foreignPassportCountryOfIssuance &&

                        <SummaryItem
                            textCode = 'common.foreign_county_issuance'
                            value = { foreignPassportCountryOfIssuance }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.foreignPassportCountryOfIssuance`
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        documentExpirationDate &&

                        <SummaryItem
                            textCode = 'common.document_expiration_date'
                            value = { getDocumentExpirationDate(documentExpirationDate) }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.documentExpirationDate`,
                                        getDocumentExpirationDate
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        i94Number &&

                        <SummaryItem
                            textCode = 'common.i94_number'
                            value={getValue(i94Number, 'i94Number')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('i94Number')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.i94Number`,
                                        getValue,
                                        ['i94Number']
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        cardNumber &&

                        <SummaryItem
                            textCode = 'common.card_number'
                            value = { cardNumber }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.cardNumber`
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        orrcertificationIndicator &&

                        <SummaryItem
                            textCode = 'common.orr_certification'
                            value = { orrcertificationIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.orrcertificationIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        cubanHaitianEntrantIndicator &&

                        <SummaryItem
                            textCode = 'common.cuban_haitian'
                            value = { cubanHaitianEntrantIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.cubanHaitianEntrantIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        orreligibilityLetterIndicator &&

                        <SummaryItem
                            textCode = 'common.refugee_resettlement'
                            value = { orreligibilityLetterIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.orreligibilityLetterIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        stayOfRemovalIndicator &&

                        <SummaryItem
                            textCode = 'common.staying_removal_issued'
                            value = { stayOfRemovalIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.stayOfRemovalIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        americanSamoanIndicator &&

                        <SummaryItem
                            textCode = 'common.resident_of_american_samoa'
                            value = { americanSamoanIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.americanSamoanIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        withholdingOfRemovalIndicator &&

                        <SummaryItem
                            textCode = 'common.withholding_removal'
                            value = { withholdingOfRemovalIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.withholdingOfRemovalIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        nameSameOnDocumentIndicator !== null &&

                        <SummaryItem
                            textCode = 'common.name_same_on_document'
                            value = { nameSameOnDocumentIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.nameSameOnDocumentIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        nameSameOnDocumentIndicator !== null && !nameSameOnDocumentIndicator &&

                        <SummaryItem
                            textCode = 'common.name_as_shown_on_document'
                            value = { normalizeNameOfPerson(nameOnDocument) }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.nameOnDocument`,
                                        normalizeNameOfPerson
                                    ]
                                ]
                            }
                        />

                    }

                    <SummaryItem
                        textCode = 'common.residence_since_1996'
                        value = { livedIntheUSSince1996Indicator }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.livedIntheUSSince1996Indicator`
                                ]
                            ]
                        }
                    />

                    {
                        unchangedImmigrationStatusLastFiveYears !== null &&

                            <SummaryItem
                                textCode = 'common.has_current_status_five_last_years'
                                value = { unchangedImmigrationStatusLastFiveYears }
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.unchangedImmigrationStatusLastFiveYears`
                                        ]
                                    ]
                                }
                            />

                    }
                </Fragment>
            }
        </Fragment>
    );
};

export default CitizenshipImmigrationStatusInfo;